/**
 * Title and meta-description rules for the location pages.
 *
 * Patterns follow what ranks #1 for these queries today (Practo, CB Physio,
 * Max): "Best Physiotherapists Near Me in <City>". One title absorbs
 * "physiotherapy <city>", "best physiotherapist in <city>", "physiotherapist
 * near me <city>" and "physiotherapy near me in <city>" at once.
 */

const BRAND = ' | Recovry';
/** Google shows ~155-160 characters of a description before truncating. */
const DESCRIPTION_MAX = 158;

/** Trim at a word boundary and never mid-sentence into an ellipsis. */
export function clampDescription(text: string, max = DESCRIPTION_MAX): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  const end = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf(', '), cut.lastIndexOf(' '));
  const kept = cut.slice(0, end > 40 ? end : max - 1).replace(/[,\s]+$/, '');
  return kept.endsWith('.') ? kept : `${kept}.`;
}

export function cityTitle(city: string): string {
  return `Best Physiotherapists Near Me in ${city}${BRAND}`;
}

export function cityH1(city: string): string {
  return `Best Physiotherapists Near Me in ${city}`;
}

export function cityDescription(city: string, clinicCount?: number, fromPrice?: number): string {
  const count = clinicCount ? `${clinicCount} verified ` : 'Verified ';
  const price = fromPrice ? ` from ₹${fromPrice}/session` : '';
  return clampDescription(
    `${count}physiotherapy clinics in ${city}${price}. Compare fees and therapists, read patient reviews, sort by distance and book online. Home visits available.`,
  );
}

export function sectorTitle(sector: string, city: string): string {
  return `Best Physiotherapists in ${sector}, ${city}${BRAND}`;
}

export function sectorH1(sector: string, city: string): string {
  return `Best Physiotherapists in ${sector}, ${city}`;
}

export function sectorDescription(sector: string, city: string, clinicCount?: number): string {
  const count = clinicCount ? `${clinicCount} verified ` : 'Verified ';
  return clampDescription(
    `${count}physiotherapy clinics in and around ${sector}, ${city}. See fees, therapists and patient reviews, then book a clinic or home visit online.`,
  );
}

const SMALL_WORDS = new Set(['and', 'or', 'of', 'in', 'for', 'the', 'a', 'to', '&', '/']);

/** Catalog names are sentence case ("Back pain therapy"); headings use Title Case. */
export function serviceDisplayName(name: string): string {
  return name
    .split(' ')
    .map((w, i) => {
      if (w.toUpperCase() === w) return w; // ACL, TENS, (ESWT)
      if (i > 0 && SMALL_WORDS.has(w.toLowerCase())) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');
}

export function serviceTitle(service: string, location: string): string {
  return `${service} in ${location}${BRAND}`;
}

export function serviceDescription(service: string, location: string): string {
  return clampDescription(
    `Verified clinics offering ${service.toLowerCase()} in ${location}. Compare fees and physiotherapists, read reviews and book a session online.`,
  );
}

/** "Physiotherapy at Home in Delhi" is the phrasing people actually search. */
export function homeVisitTitle(city: string): string {
  return `Physiotherapy at Home in ${city} | Book a Home Visit${BRAND}`;
}

export function homeVisitH1(city: string): string {
  return `Physiotherapy at Home in ${city}`;
}

export function homeVisitDescription(city: string): string {
  return clampDescription(
    `Book a verified physiotherapist to visit you at home in ${city}. Compare home-visit fees and reviews, choose a time that suits you and confirm online.`,
  );
}
